

import React, { useEffect, useState } from "react";
import {
  guideLogin,
  guideForgotPassword,
  guideVerifyOtp,
  guideResetPassword,
} from "../services/guideService";
import { saveUserData } from "../utils/storage";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import "./GuideAuth.css";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const GuideAuth = ({ show, onClose, setShowGuideLogin }) => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const [step,            setStep]            = useState(1);
  const [showPassword,    setShowPassword]    = useState(false);
  const [emailOtp,        setEmailOtp]        = useState("");
  const [loading,         setLoading]         = useState(false);
  const [resendTimer,     setResendTimer]     = useState(0);
  const [resending,       setResending]       = useState(false);
  const [errorMessage,    setErrorMessage]    = useState(null);
  const [successMessage,  setSuccessMessage]  = useState(null);

  const [formData, setFormData] = useState({
    email: "", password: "", newPassword: "", confirmPassword: "",
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const closeModal = () => {
    setShowGuideLogin(false);
    onClose?.();
  };

  // ── Auto-clear messages ──────────────────────────────────────────────
  useEffect(() => {
    if (!errorMessage) return;
    const t = setTimeout(() => setErrorMessage(null), 5000);
    return () => clearTimeout(t);
  }, [errorMessage]);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // ── Resend OTP countdown ─────────────────────────────────────────────
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((p) => p - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleLogin = async () => {
    try {
      setLoading(true);
      const res = await guideLogin(formData);
      if (res.success) {
        dispatch(loginSuccess({ user: res.user, token: res.token }));
        saveUserData("guide",              res.user);
        saveUserData("guide_token",        res.token);
        saveUserData("guide_refreshtoken", res.refreshToken);
        saveUserData("guide_login_time",   Date.now());
        navigate("/guide-dashboard");
        closeModal();
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    try {
      setLoading(true);
      await guideForgotPassword({ email: formData.email });
      setStep(3);
      setResendTimer(120);
    } catch (err) {
      setErrorMessage(err.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setResending(true);
      await guideForgotPassword({ email: formData.email });
      setResendTimer(120);
      setSuccessMessage("OTP resent successfully.");
    } catch (err) {
      setErrorMessage(err.response?.data?.message);
    } finally {
      setResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);
      await guideVerifyOtp({ email: formData.email, otp: emailOtp });
      setStep(4);
    } catch (err) {
      setErrorMessage(err.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setLoading(true);
      await guideResetPassword({
        email:           formData.email,
        newPassword:     formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      setSuccessMessage("Password reset successfully.");
      setStep(1);
    } catch (err) {
      setErrorMessage(err.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const stepLabels = ["Login", "Forgot Password", "Verify OTP", "Reset Password"];

  return (
    <div
      className="ga-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
    >
      <div className="ga-box">

        {/* ── Header ── */}
        <div className="ga-header">
          <div className="ga-header-left">
            <div className="ga-logo-dot">🧭</div>
            <div>
              <div className="ga-header-title">Guide</div>
              <div className="ga-header-sub">{stepLabels[step - 1]}</div>
            </div>
          </div>
          <button className="ga-close" onClick={closeModal}>×</button>
        </div>

        {/* ── Step indicator ── */}
        <div className="ga-steps">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`ga-step ${step >= s ? "ga-step--done" : ""}`} />
          ))}
        </div>

        {/* ── Body ── */}
        <div className="ga-body">

          {/* Messages */}
          {errorMessage && (
            <div className="ga-alert ga-alert--error">{errorMessage}</div>
          )}
          {successMessage && (
            <div className="ga-alert ga-alert--success">{successMessage}</div>
          )}

          {/* ─── STEP 1 — Login ─── */}
          {step === 1 && (
            <>
              <p className="ga-tagline">
                Manage your bookings and clients with <strong>Safarix</strong>
              </p>

              <div className="ga-field">
                <label className="ga-label">Email address</label>
                <input
                  className="ga-input"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="ga-field">
                <label className="ga-label">Password</label>
                <div className="ga-input-wrap">
                  <input
                    className="ga-input"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    className="ga-eye"
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    tabIndex={-1}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
              </div>

              <button
                className="ga-btn ga-btn--primary"
                disabled={!isValidEmail(formData.email) || !formData.password || loading}
                onClick={handleLogin}
              >
                {loading ? <span className="ga-spinner" /> : null}
                {loading ? "Signing in…" : "Login"}
              </button>

              <div className="ga-links">
                <button className="ga-link" onClick={() => setStep(2)}>
                  Forgot password?
                </button>
                <button
                  className="ga-link"
                  onClick={() => { closeModal(); navigate("/guide-careers"); }}
                >
                  New Guide? Apply →
                </button>
              </div>
            </>
          )}

          {/* ─── STEP 2 — Enter email ─── */}
          {step === 2 && (
            <>
              <p className="ga-tagline">
                Enter your registered email to receive an OTP.
              </p>

              <div className="ga-field">
                <label className="ga-label">Email address</label>
                <input
                  className="ga-input"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <button
                className="ga-btn ga-btn--primary"
                disabled={!isValidEmail(formData.email) || loading}
                onClick={handleSendOtp}
              >
                {loading ? <span className="ga-spinner" /> : null}
                {loading ? "Sending…" : "Send OTP"}
              </button>

              <div className="ga-links">
                <button className="ga-link" onClick={() => setStep(1)}>
                  ← Back to login
                </button>
              </div>
            </>
          )}

          {/* ─── STEP 3 — OTP ─── */}
          {step === 3 && (
            <>
              <p className="ga-tagline">
                We've sent a 4-digit code to <strong>{formData.email}</strong>
              </p>

              <div className="ga-field">
                <label className="ga-label">OTP Code</label>
                <input
                  className="ga-input ga-input--otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="• • • •"
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.replace(/\D/, ""))}
                />
              </div>

              <button
                className="ga-btn ga-btn--primary"
                disabled={emailOtp.length !== 4 || loading}
                onClick={handleVerifyOtp}
              >
                {loading ? <span className="ga-spinner" /> : null}
                {loading ? "Verifying…" : "Verify OTP"}
              </button>

              <div className="ga-resend">
                {resendTimer > 0 ? (
                  <span className="ga-resend-timer">
                    Resend OTP in {Math.floor(resendTimer / 60)}:
                    {String(resendTimer % 60).padStart(2, "0")}
                  </span>
                ) : (
                  <button
                    className="ga-link"
                    disabled={resending}
                    onClick={handleResendOtp}
                  >
                    {resending ? "Resending…" : "Resend OTP"}
                  </button>
                )}
              </div>
            </>
          )}

          {/* ─── STEP 4 — Reset password ─── */}
          {step === 4 && (
            <>
              <p className="ga-tagline">Choose a strong new password.</p>

              <div className="ga-field">
                <label className="ga-label">New Password</label>
                <div className="ga-input-wrap">
                  <input
                    className="ga-input"
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    placeholder="New password"
                    value={formData.newPassword}
                    onChange={handleChange}
                  />
                  <button
                    className="ga-eye"
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    tabIndex={-1}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
              </div>

              <div className="ga-field">
                <label className="ga-label">Confirm Password</label>
                <input
                  className="ga-input"
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>

              <button
                className="ga-btn ga-btn--primary"
                disabled={!formData.newPassword || !formData.confirmPassword || loading}
                onClick={handleResetPassword}
              >
                {loading ? <span className="ga-spinner" /> : null}
                {loading ? "Resetting…" : "Reset Password"}
              </button>
            </>
          )}

        </div>
        {/* end ga-body */}

        {/* ── Footer ── */}
        <div className="ga-footer">
          By signing in, you accept our{" "}
          <a href="/terms-conditions" className="ga-footer-link">Terms & Conditions</a>
          {" "}and{" "}
          <a href="/privacy-policy" className="ga-footer-link">Privacy Policy</a>.
        </div>

      </div>
    </div>
  );
};

export default GuideAuth;